import { useEffect, useState, useCallback } from "react";
import { Plus, Search, RefreshCw, Edit2, Key, ShieldOff, ShieldCheck, Trash2, X, Mail, Phone as PhoneIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatDateTimeBR } from "../../lib/api";
import { maskPhoneBR, sanitizePhone } from "../../lib/phone";
import { useAuth } from "../../context/AuthContext";

// RC15 — esta tela lista SOMENTE funcionários (perfis internos). Clientes ficam em /admin/clientes.
const ROLES = [
  { value: "super_admin", label: "Owner",         hint: "Controle total da plataforma" },
  { value: "admin",       label: "Administrador", hint: "Administra o coworking" },
  { value: "manager",     label: "Gerência",      hint: "Reservas, clientes, CRM, relatórios" },
  { value: "operator",    label: "Operador",      hint: "Reservas + check-in/out" },
];

const ROLE_COLOR = {
  super_admin: "bg-amber-100 text-amber-800 border-amber-200",
  admin:       "bg-blue-100 text-blue-800 border-blue-200",
  manager:     "bg-purple-100 text-purple-800 border-purple-200",
  operator:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  client:      "bg-gray-100 text-gray-700 border-gray-200",
};

function RoleBadge({ role, label }) {
  const cls = ROLE_COLOR[role] || "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase font-bold tracking-wider ${cls}`} data-testid={`role-badge-${role}`}>
      {label || role}
    </span>
  );
}

const emptyForm = {
  name: "", last_name: "", email: "", phone: "", password: "", role: "operator", active: true,
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit' | 'reset', user? }
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const isOwner = me?.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (role) params.set("role", role);
      if (status) params.set("status", status);
      params.set("page_size", "100");
      const { data } = await api.get(`/users?${params.toString()}`);
      // RC15 — somente perfis internos (clientes ficam em /admin/clientes)
      const staffOnly = (data.items || []).filter((u) => u.role !== "client");
      setItems(staffOnly);
      setTotal(staffOnly.length);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, [q, role, status]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setModal({ mode: "create" }); };
  const openEdit = (u) => { setForm({ ...emptyForm, ...u, password: "" }); setModal({ mode: "edit", user: u }); };
  const openReset = (u) => { setForm({ ...emptyForm, password: "" }); setModal({ mode: "reset", user: u }); };

  const closeModal = () => setModal(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "create") {
        const body = {
          name: form.name.trim(),
          last_name: (form.last_name || "").trim(),
          email: form.email.trim().toLowerCase(),
          phone: sanitizePhone(form.phone),
          password: form.password,
          role: form.role,
          active: !!form.active,
        };
        await api.post("/users", body);
        toast.success("Usuário criado");
      } else if (modal.mode === "edit") {
        const body = {
          name: form.name.trim(),
          last_name: (form.last_name || "").trim(),
          email: form.email.trim().toLowerCase(),
          phone: sanitizePhone(form.phone),
          role: form.role,
        };
        await api.put(`/users/${modal.user.id}`, body);
        toast.success("Usuário atualizado");
      } else if (modal.mode === "reset") {
        await api.post(`/users/${modal.user.id}/reset-password`, { new_password: form.password });
        toast.success("Senha redefinida");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (u) => {
    if (!confirm(`${u.active ? "Desativar" : "Ativar"} ${u.name}?`)) return;
    try {
      await api.patch(`/users/${u.id}/status`, { active: !u.active });
      toast.success(u.active ? "Usuário desativado" : "Usuário ativado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao alterar status");
    }
  };

  const del = async (u) => {
    if (!confirm(`Excluir permanentemente ${u.name}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário excluído");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[#0A1128] font-light">Funcionários</h1>
          <p className="text-sm text-gray-500 mt-1">
            Equipe interna com acesso à plataforma · {total} funcionário(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold uppercase tracking-wider hover:bg-gray-50" data-testid="users-refresh">
            <RefreshCw size={14} /> Atualizar
          </button>
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1128] px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#c9a534]" data-testid="users-create-btn">
            <Plus size={14} /> Novo usuário
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, e-mail ou telefone…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#D4AF37] outline-none"
            data-testid="users-search"
          />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="users-filter-role">
          <option value="">Todos perfis</option>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="users-filter-status">
          <option value="">Todos status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="users-table">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Usuário</th>
                <th className="text-left px-4 py-3">Contato</th>
                <th className="text-left px-4 py-3">Perfil</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Último acesso</th>
                <th className="text-left px-4 py-3">Criado em</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400 text-sm">Carregando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400 text-sm">Nenhum usuário encontrado</td></tr>
              )}
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0A1128]">{u.name} {u.last_name || ""}</div>
                    <div className="text-[10px] text-gray-400 font-mono">{u.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-700"><Mail size={12} /> {u.email}</div>
                    {u.phone && <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5"><PhoneIcon size={12} /> {u.phone}</div>}
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} label={u.role_label} /></td>
                  <td className="px-4 py-3">
                    {u.active === false ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 text-[10px] uppercase font-bold">Inativo</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase font-bold">Ativo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{u.last_login_at ? formatDateTimeBR(u.last_login_at) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{u.created_at ? formatDateTimeBR(u.created_at) : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(u)} title="Editar" className="p-1.5 rounded hover:bg-gray-100" data-testid={`user-edit-${u.id}`}>
                        <Edit2 size={14} className="text-gray-600" />
                      </button>
                      <button onClick={() => openReset(u)} title="Redefinir senha" className="p-1.5 rounded hover:bg-gray-100" data-testid={`user-reset-${u.id}`}>
                        <Key size={14} className="text-gray-600" />
                      </button>
                      <button onClick={() => toggleStatus(u)} title={u.active === false ? "Ativar" : "Desativar"} className="p-1.5 rounded hover:bg-gray-100" data-testid={`user-toggle-${u.id}`}>
                        {u.active === false
                          ? <ShieldCheck size={14} className="text-emerald-600" />
                          : <ShieldOff size={14} className="text-amber-600" />}
                      </button>
                      {isOwner && u.role !== "super_admin" && u.id !== me?.id && (
                        <button onClick={() => del(u)} title="Excluir" className="p-1.5 rounded hover:bg-red-50" data-testid={`user-delete-${u.id}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeModal} data-testid="user-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-[#0A1128]">
                {modal.mode === "create" && "Novo usuário"}
                {modal.mode === "edit" && `Editar ${modal.user.name}`}
                {modal.mode === "reset" && `Redefinir senha — ${modal.user.name}`}
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="user-form">
              {modal.mode !== "reset" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Nome</label>
                      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="user-form-name" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Sobrenome</label>
                      <input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="user-form-lastname" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">E-mail</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="user-form-email" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Telefone</label>
                    <input type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhoneBR(e.target.value) })} placeholder="(21) 99999-9999" className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="user-form-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Perfil</label>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="user-form-role">
                      {ROLES.filter(r => isOwner || r.value !== "super_admin").map(r => (
                        <option key={r.value} value={r.value}>{r.label} — {r.hint}</option>
                      ))}
                    </select>
                  </div>
                  {modal.mode === "create" && (
                    <div className="flex items-center gap-2">
                      <input id="user-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="user-form-active" />
                      <label htmlFor="user-active" className="text-xs text-gray-700">Conta ativa</label>
                    </div>
                  )}
                </>
              )}
              {(modal.mode === "create" || modal.mode === "reset") && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                    {modal.mode === "create" ? "Senha inicial" : "Nova senha"}
                  </label>
                  <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm font-mono" data-testid="user-form-password" />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Mínimo 8 caracteres, com maiúscula, minúscula, número e símbolo.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="bg-[#0A1128] text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#14213D] disabled:opacity-50" data-testid="user-form-submit">
                  {saving ? "Salvando…" : (modal.mode === "reset" ? "Redefinir" : "Salvar")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
